import { zodResolver } from "@hookform/resolvers/zod";
import { observer } from "mobx-react-lite";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { OptionType, useGetOptionsQuery } from "@/api/admin";
import { CustomCardBody, Loader } from "@/components/atoms";
import {
  SelectOptionsWithoutValidation,
  SelectOptionsWithValidation,
} from "@/components/molecules";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import { rootStore } from "@/store/RootStore";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";
import { wordToUpper } from "@/utils/wordToUpper";

import { BrandAmenitiesSchema, type BrandAmenitiesType } from "./types";

type BrandFormAmenitiesProps = FormProps<BrandAmenitiesType> & {
  draftEntityId?: string;
};

const BrandFormAmenitiesInner = forwardRef<
  FormRefType<BrandAmenitiesType>,
  BrandFormAmenitiesProps
>((props, ref) => {
  const {
    defaultValues,
    onSubmit,
    onValidityChange,
    id,
    classes,
    draftEntityId,
  } = props;
  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Amenity,
  ]);

  const amenityGroups = (optionsData?.amenity ?? []).map(group => ({
    ...group,
    name: wordToUpper(group.name),
    values: group.values.map(v => ({
      ...v,
      name: wordToUpper(v.name),
    })),
  }));

  const form = useForm<BrandAmenitiesType>({
    resolver: zodResolver(BrandAmenitiesSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  useWizardStepDraft({
    form,
    wizard: "branch",
    entityId: draftEntityId,
    step: "amenities",
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = form;

  useExposeFormValues(ref, form);
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const { isOnboarding, isRestaurant, isAdmin } = rootStore.authStore;

  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(4);

  useEffect(() => {
    const updateColumns = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const colCount = Math.floor(containerWidth / 220);
      setColumns(colCount || 1);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  if (isOptionPending) {
    return <Loader fullscreen />;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id={id}
        data-testid="brand-form-amenities"
      >
        <CustomCardBody classes={cn(classes)}>
          {(isOnboarding || isRestaurant) && (
            <div
              className={cn(
                "grid grid-cols-2 items-start gap-6",
                isRestaurant && "max-w-[512px]"
              )}
            >
              {amenityGroups.map(group => (
                <SelectOptionsWithValidation<BrandAmenitiesType>
                  key={group.name}
                  name="amenities"
                  form={control}
                  title={group.name}
                  selectList={group.values}
                />
              ))}
            </div>
          )}
          {isAdmin && (
            <div
              ref={containerRef}
              className="grid [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
            >
              {amenityGroups.map((group, index) => (
                <div
                  key={group.name}
                  className={cn(
                    "px-0 py-3 sm:px-5 sm:py-5 border-r border-b border-border",
                    (index + 1) % columns === 0 && "border-r-0",
                    index >=
                      amenityGroups.length -
                        (amenityGroups.length % columns || columns) &&
                      "border-b-0"
                  )}
                >
                  <SelectOptionsWithoutValidation<BrandAmenitiesType>
                    name="amenities"
                    form={control}
                    title={group.name}
                    selectList={group.values}
                  />
                </div>
              ))}
            </div>
          )}
        </CustomCardBody>
      </form>
    </Form>
  );
});

BrandFormAmenitiesInner.displayName = "BrandFormAmenities";

const BrandFormAmenities = observer(BrandFormAmenitiesInner);
export default BrandFormAmenities;
